import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Button, Modal } from '@canaveral/components';
import Media from './Media';
import { LimitContextProvider } from '../../../../admin/contexts/LimitContext';
import MediaGallerySelector from '../../../../admin/containers/MediaGallerySelector';

const propTypes = {
  alignmentEnabled: PropTypes.bool,
  data: PropTypes.shape({
    mediumUpload: PropTypes.shape({
      medium: PropTypes.object,
      userErrors: PropTypes.array,
    }).isRequired,
  }),
  images: PropTypes.arrayOf(PropTypes.shape({
    mediumId: PropTypes.string,
    file: PropTypes.string,
    filename: PropTypes.string,
    alt: PropTypes.string,
    title: PropTypes.string,
    link: PropTypes.string,
    target: PropTypes.string,
  })).isRequired,
  limit: PropTypes.number,
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  uploadedFile: PropTypes.shape({
    file: PropTypes.string.isRequired,
  }),
  widgetName: PropTypes.string.isRequired,
};

const defaultProps = {
  alignmentEnabled: undefined,
  data: undefined,
  limit: undefined,
  loading: undefined,
  uploadedFile: undefined,
};

const Editor = ({
  alignmentEnabled,
  alignment: initialAlignment,
  data,
  images: initialImages,
  limit,
  loading,
  onCancel,
  onSave,
  onUpload,
  uploadedFile,
  widgetName,
}) => {
  const [showGallery, setShowGallery] = useState(false);
  const [selectedMedium, setSelectedMedium] = useState();
  const [images, setImages] = useState(initialImages);
  const [alignment, setAlignment] = useState(initialAlignment);

  // Add actual clone for starting data reference
  const [startingValues] = useState(initialImages.map(item => ({ ...item })));
  const [initialValues, setInitialValues] = useState([...initialImages]);
  const [error, setError] = useState();
  const [usedMedia, setUsedMedia] = useState();
  useEffect(() => {
    if (data && data.mediumUpload.medium) {
      setImages([
        ...images,
        { ...data.mediumUpload.medium, mediumId: data.mediumUpload.medium.id },
      ]);
    } else if (data && data.mediumUpload.userErrors.length) {
      setError(data.mediumUpload.userErrors.map(err => err.message));
    }
  }, [data]);

  useEffect(() => {
    if (uploadedFile && uploadedFile.file) {
      setImages([...images, { ...uploadedFile }]);
    }
  }, [uploadedFile]);

  useEffect(() => {
    setError();
    const media = images.filter(image => image.mediumId).map(image => image.mediumId);
    setUsedMedia(media);
  }, [images]);

  const onClickChooseImage = () => {
    setImages([...images, selectedMedium]);
    setInitialValues(prevValues => [...prevValues, selectedMedium]);
    setSelectedMedium(null);
    setShowGallery(false);
  };

  const handleDelete = index => () => {
    const newImages = [...images];
    const removedImage = newImages.splice(index, 1);
    setImages(newImages);

    if (removedImage) {
      const modifiedIndex = initialValues.findIndex(val => val.mediumId === removedImage.mediumId);
      const newInitialValues = [...initialValues];
      if (modifiedIndex > -1) {
        newInitialValues.splice(modifiedIndex, 1); setInitialValues(newInitialValues);
      }
    }
  };

  const handleMove = (index, modifier) => () => {
    const newImages = [...images];
    newImages.splice(index + modifier, 0, newImages.splice(index, 1)[0]);
    setImages(newImages);
  };

  const changeValue = (index, valueName, value) => {
    const newImages = [...images];
    newImages[index][valueName] = value;
    setImages(newImages);

    const modifiedImage = newImages[index];
    const unmodified = initialValues.some(val => val.mediumId !== modifiedImage.mediumId);
    if (unmodified) { setInitialValues(prevValues => [...prevValues, modifiedImage]); }
  };

  const handleAltChange = index => ({ target }) => {
    changeValue(index, 'alt', target.value);
  };

  const handleDecorativeChange = index => ({ target }) => {
    changeValue(index, 'decorative', target.checked);
  };

  const handleTitleChange = index => ({ target }) => {
    changeValue(index, 'title', target.value);
  };

  const handleHrefChange = index => ({ target }) => {
    changeValue(index, 'link', target.value);
  };

  const setTarget = index => ({ target }) => {
    changeValue(index, 'target', target.value);
  };

  const handleWidthChange = index => ({ target }) => {
    changeValue(index, 'width', target.value);
  };

  const handleHeightChange = index => ({ target }) => {
    changeValue(index, 'height', target.value);
  };

  const handleAlignmentChange = (value) => {
    setAlignment(value);
  };

  const onSubmit = () => {
    if (images.some(({ alt, decorative }) => !decorative && !alt)) {
      setError('All non-decorative images require an alt text.');
    } else { onSave({ images, initialValues: startingValues, alignment }); }
  };

  return (
    <Modal onDismiss={onCancel} show size="xl">
      <Modal.Header>
        <Modal.Title id="image-editor-modal">
          {showGallery ? 'Media Gallery' : widgetName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showGallery ? (
          <LimitContextProvider>
            <MediaGallerySelector
              setSelectedMedium={setSelectedMedium}
              selectedMedium={selectedMedium}
              usedMedia={usedMedia}
              showTrash={false}
              showSearch
            />
          </LimitContextProvider>
        ) : (
          <Media
            alignment={alignment}
            alignmentEnabled={alignmentEnabled}
            error={error}
            handleAlignmentChange={handleAlignmentChange}
            handleAltChange={handleAltChange}
            handleDecorativeChange={handleDecorativeChange}
            handleDelete={handleDelete}
            handleHrefChange={handleHrefChange}
            handleMove={handleMove}
            handleTitleChange={handleTitleChange}
            handleWidthChange={handleWidthChange}
            handleHeightChange={handleHeightChange}
            images={images}
            limit={limit}
            loading={loading}
            onUpload={onUpload}
            selectedMedium={selectedMedium}
            setImages={setImages}
            setSelectedMedium={setSelectedMedium}
            setShowGallery={setShowGallery}
            setTarget={setTarget}
            showGallery={showGallery}
            usedMedia={usedMedia}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={showGallery ? () => setShowGallery(false) : onCancel}
          context="neutral"
        >
          Cancel
        </Button>
        <Button
          context="positive"
          disabled={showGallery && !selectedMedium}
          onClick={showGallery ? onClickChooseImage : onSubmit}
        >
          {showGallery ? 'Choose Image' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

Editor.propTypes = propTypes;
Editor.defaultProps = defaultProps;

Editor.fragments = {
  media: gql`
    fragment ImageWidgetEditor on Medium {
      companyId
      filename
      decorative
      trash
      url
      formattedUpdatedAt
      defaultMetadata {
        title
        altText
      }
      metadata(locale: $locale) {
        id
        locale
        altText
        title
      }
    }
  `,
};

export default Editor;
